/**
 * Degradation Curve Service
 *
 * Provides degradation models for lifetime savings calculations.
 * Two models available:
 * 1. ASHRAE Model - Industry standard ~2% per year degradation
 * 2. Corpus Model - Derived from 82 PE-certified measurements with equipment age data
 *
 * CRITICAL UNDERSTANDING:
 * PermaFrost NMR restores close to 100% of cooling capacity lost to thermal
 * degradation. The "improvement %" in our corpus IS the degradation that existed.
 * Therefore, our corpus data directly measures real-world degradation by age.
 *
 * RESEARCH SOURCES:
 * - UCF/FSEC Study: Found 2% COP decline per year over 15 years
 *   https://publications.energyresearch.ucf.edu/wp-content/uploads/2018/09/FSEC-PF-474-18.pdf
 * - MDPI Energy Study: Heat source equipment decreased 34-52% efficiency over 15 years
 *   https://www.mdpi.com/1996-1073/15/23/9217
 * - ASHRAE Equipment Life: Commercial chillers 20-25 year median life
 *   https://www.naturalhandyman.com/iip/infhvac/ASHRAE_Chart_HVAC_Life_Expectancy.pdf
 */

/**
 * Degradation by equipment age from corpus data
 * Source: 82 case studies with both kW/ton measurements AND equipment age
 * The "improvement %" represents the thermal degradation that existed before treatment
 */
export const DEGRADATION_BY_AGE = {
  '0-5': {
    avgDegradation: 20.9,
    n: 12,
    stdDev: 12.5,  // Verified from corpus 2024-12-31
    interpretation: 'New equipment with minimal thermal degradation accumulated'
  },
  '5-10': {
    avgDegradation: 25.2,
    n: 21,
    stdDev: 12.2,
    interpretation: 'Moderate degradation, typical maintenance schedules'
  },
  '10-15': {
    avgDegradation: 27.4,
    n: 32,
    stdDev: 12.0,
    interpretation: 'Peak degradation window - best ROI for treatment'
  },
  '15-20': {
    avgDegradation: 24.9,
    n: 9,
    stdDev: 13.0,
    interpretation: 'Degradation plateaus, mechanical limits may factor'
  },
  '20+': {
    avgDegradation: 22.6,
    n: 8,
    stdDev: 11.6,
    interpretation: 'Legacy equipment, verify mechanical soundness before treatment'
  }
}

/**
 * Treatment cost per ton - from corpus financial data
 * Should be updated if pricing changes
 */
export const TREATMENT_COST_PER_TON = 125

/**
 * Total sample size for age-based analysis
 */
export const AGE_DATA_SAMPLE_SIZE = Object.values(DEGRADATION_BY_AGE)
  .reduce((sum, bucket) => sum + bucket.n, 0)

/**
 * ASHRAE-based degradation model
 * Industry standard: ~2% efficiency loss per year
 * Plateaus at approximately 30% maximum degradation
 *
 * @param {number} ageYears - Equipment age in years
 * @returns {number} Expected degradation as decimal (0.20 = 20%)
 */
export function ashraeDegradation(ageYears) {
  const annualRate = 0.02  // 2% per year (UCF/FSEC study)
  const maxDegradation = 0.30  // 30% maximum before mechanical failure likely
  return Math.min(ageYears * annualRate, maxDegradation)
}

/**
 * Corpus-derived degradation model
 * Fitted curve from 82 PE-certified field measurements
 * More accurate than ASHRAE for real-world HVAC systems
 *
 * Curve characteristics:
 * - Starts at ~15% for year 1 (initial oil fouling)
 * - Increases ~1.2% per year for years 1-5
 * - Increases ~0.5% per year for years 5-15 (rate slows)
 * - Slight decline after 15 years (selection bias - only functional units measured)
 *
 * @param {number} ageYears - Equipment age in years
 * @returns {number} Expected degradation as decimal (0.25 = 25%)
 */
export function corpusDegradation(ageYears) {
  if (ageYears <= 0) return 0.10  // Minimal initial degradation
  if (ageYears <= 5) return 0.15 + (ageYears * 0.012)  // ~15-21%
  if (ageYears <= 15) return 0.21 + ((ageYears - 5) * 0.005)  // ~21-26%
  // After 15 years, slight decline (selection bias - broken units replaced)
  return Math.max(0.20, 0.26 - ((ageYears - 15) * 0.003))
}

/**
 * Get degradation estimate with uncertainty bounds
 *
 * @param {number} ageYears - Equipment age in years
 * @param {string} model - 'corpus' or 'ashrae'
 * @returns {Object} Degradation estimate with bounds
 */
export function getDegradationEstimate(ageYears, model = 'corpus') {
  const ageBucket = getAgeBucket(ageYears)
  const bucketData = DEGRADATION_BY_AGE[ageBucket]

  if (model === 'ashrae') {
    const degradation = ashraeDegradation(ageYears)
    return {
      model: 'ASHRAE',
      expected: degradation,
      expectedPct: (degradation * 100).toFixed(1),
      source: 'ASHRAE/UCF-FSEC industry standard (~2%/year)',
      note: 'Theoretical model, may underestimate real-world degradation'
    }
  }

  // Corpus model with uncertainty from bucket data
  const degradation = corpusDegradation(ageYears)
  const stdDev = bucketData ? bucketData.stdDev / 100 : 0.10

  return {
    model: 'Corpus',
    expected: degradation,
    expectedPct: (degradation * 100).toFixed(1),
    floor: Math.max(0.05, degradation - stdDev),
    floorPct: (Math.max(0.05, degradation - stdDev) * 100).toFixed(1),
    optimistic: Math.min(0.45, degradation + stdDev),
    optimisticPct: (Math.min(0.45, degradation + stdDev) * 100).toFixed(1),
    n: bucketData ? bucketData.n : null,
    stdDev: stdDev,
    stdDevPct: (stdDev * 100).toFixed(1),
    source: `${AGE_DATA_SAMPLE_SIZE} PE-certified field measurements`,
    note: bucketData ? bucketData.interpretation : 'Interpolated from corpus data'
  }
}

/**
 * Get the age bucket key for a given age
 */
function getAgeBucket(ageYears) {
  if (ageYears <= 5) return '0-5'
  if (ageYears <= 10) return '5-10'
  if (ageYears <= 15) return '10-15'
  if (ageYears <= 20) return '15-20'
  return '20+'
}

/**
 * ASHRAE equipment expected life by type
 * Source: ASHRAE Equipment Life Expectancy Chart
 */
export const EQUIPMENT_LIFE_YEARS = {
  'Chiller': {
    centrifugal: 25,
    screw: 20,
    reciprocating: 20,
    absorption: 23,
    default: 23
  },
  'RTU': {
    default: 15
  },
  'CRAC': {
    default: 15
  },
  'Split System': {
    default: 15
  },
  'DX System': {
    default: 15
  },
  'Refrigeration': {
    commercial: 15,
    industrial: 20,
    default: 17
  }
}

/**
 * Get expected equipment life
 */
export function getExpectedLife(equipmentType, subtype = 'default') {
  const typeData = EQUIPMENT_LIFE_YEARS[equipmentType]
  if (!typeData) return 15  // Industry default
  return typeData[subtype] || typeData.default || 15
}

/**
 * Calculate lifetime savings projection
 *
 * @param {Object} params - Input parameters
 * @param {number} params.capacityTons - Equipment capacity in tons
 * @param {number} params.currentAgeYears - Current equipment age
 * @param {number} params.originalKwTon - Original rated kW/ton (or null to estimate)
 * @param {number} params.runtimeHoursPerYear - Annual runtime hours
 * @param {number} params.electricityRate - $/kWh
 * @param {string} params.equipmentType - Equipment type for life expectancy
 * @param {string} params.model - 'corpus' or 'ashrae'
 * @returns {Object} Lifetime savings projection
 */
export function calculateLifetimeSavings({
  capacityTons,
  currentAgeYears,
  originalKwTon = null,
  runtimeHoursPerYear = 2500,
  electricityRate = 0.12,
  equipmentType = 'Chiller',
  model = 'corpus'
}) {
  // Get degradation estimate
  const degradation = getDegradationEstimate(currentAgeYears, model)

  // Estimate original kW/ton if not provided
  const baseKwTon = originalKwTon || getTypicalKwTon(equipmentType)

  // Current degraded performance
  const degradedKwTon = baseKwTon * (1 + degradation.expected)

  // Restored performance (treatment restores to near-design)
  // Model: restore to 98% of original (2% residual per corpus data)
  const restoredKwTon = baseKwTon * 1.02

  // Energy reduction per ton-hour
  const kwReductionPerTonHour = degradedKwTon - restoredKwTon

  // Annual energy savings
  const annualKwhSavings = kwReductionPerTonHour * capacityTons * runtimeHoursPerYear

  // Annual dollar savings
  const annualDollarSavings = annualKwhSavings * electricityRate

  // Remaining equipment life
  const expectedLife = getExpectedLife(equipmentType)
  const remainingLife = Math.max(1, expectedLife - currentAgeYears)

  // Lifetime savings
  const lifetimeSavings = annualDollarSavings * remainingLife

  // Treatment cost estimate (from corpus financial data)
  const treatmentCost = capacityTons * TREATMENT_COST_PER_TON

  // ROI calculations
  const paybackYears = annualDollarSavings > 0 ? treatmentCost / annualDollarSavings : null
  const lifetimeROI = treatmentCost > 0 ? ((lifetimeSavings - treatmentCost) / treatmentCost) * 100 : 0

  return {
    inputs: {
      capacityTons,
      currentAgeYears,
      originalKwTon: baseKwTon,
      runtimeHoursPerYear,
      electricityRate,
      equipmentType
    },
    degradation: {
      model: degradation.model,
      expectedPct: degradation.expectedPct,
      source: degradation.source
    },
    performance: {
      originalKwTon: baseKwTon.toFixed(3),
      degradedKwTon: degradedKwTon.toFixed(3),
      restoredKwTon: restoredKwTon.toFixed(3),
      kwReductionPerTonHour: kwReductionPerTonHour.toFixed(3)
    },
    annualSavings: {
      kwhSaved: Math.round(annualKwhSavings),
      dollarSaved: Math.round(annualDollarSavings)
    },
    lifetime: {
      expectedLifeYears: expectedLife,
      remainingLifeYears: remainingLife,
      totalSavings: Math.round(lifetimeSavings)
    },
    roi: {
      treatmentCost: Math.round(treatmentCost),
      paybackYears: paybackYears ? paybackYears.toFixed(2) : 'N/A',
      paybackMonths: paybackYears ? (paybackYears * 12).toFixed(1) : 'N/A',
      lifetimeROIPct: Math.round(lifetimeROI)
    }
  }
}

/**
 * Get typical kW/ton rating by equipment type
 * Source: ASHRAE Standard 90.1-2019, AHRI certification data
 */
export function getTypicalKwTon(equipmentType) {
  const typicalRatings = {
    'Chiller': 0.65,       // Typical centrifugal chiller (corpus average age ~12 yrs)
    'RTU': 1.20,           // Rooftop unit (lower efficiency)
    'CRAC': 1.10,          // Computer room AC
    'Split System': 1.15,  // Split system
    'DX System': 1.10,     // Direct expansion
    'Refrigeration': 1.30  // Commercial refrigeration
  }
  return typicalRatings[equipmentType] || 1.00
}

/**
 * Generate year-by-year projection data for charts (RESTORATIVE mode)
 * For equipment with existing degradation that gets restored
 */
export function generateProjectionData({
  capacityTons,
  currentAgeYears,
  originalKwTon = null,
  runtimeHoursPerYear = 2500,
  electricityRate = 0.12,
  equipmentType = 'Chiller'
}) {
  const expectedLife = getExpectedLife(equipmentType)
  const remainingYears = Math.max(1, expectedLife - currentAgeYears)
  const baseKwTon = originalKwTon || getTypicalKwTon(equipmentType)

  const projectionYears = []
  let cumulativeSavings = 0
  const treatmentCost = capacityTons * TREATMENT_COST_PER_TON

  for (let year = 1; year <= remainingYears; year++) {
    // FIX: Use age at each projection year, not constant current age
    const ageAtYear = currentAgeYears + year
    const degradation = corpusDegradation(ageAtYear)
    const degradedKwTon = baseKwTon * (1 + degradation)
    const restoredKwTon = baseKwTon * 1.02
    const kwReduction = degradedKwTon - restoredKwTon
    const annualKwh = kwReduction * capacityTons * runtimeHoursPerYear
    const annualSavings = annualKwh * electricityRate

    cumulativeSavings += annualSavings

    projectionYears.push({
      year,
      ageAtYear,
      degradationPct: (degradation * 100).toFixed(1),
      annualSavings: Math.round(annualSavings),
      cumulativeSavings: Math.round(cumulativeSavings),
      netSavings: Math.round(cumulativeSavings - treatmentCost),
      paybackReached: cumulativeSavings >= treatmentCost
    })
  }

  return {
    mode: 'RESTORATIVE',
    treatmentCost,
    projectionYears,
    paybackYear: projectionYears.find(y => y.paybackReached)?.year || null,
    totalLifetimeSavings: Math.round(cumulativeSavings),
    lifetimeROI: Math.round(((cumulativeSavings - treatmentCost) / treatmentCost) * 100)
  }
}

/**
 * Calculate ROI for PREVENTIVE treatment (Day 1 or post-service)
 *
 * This calculates AVOIDED future degradation, not restored existing degradation.
 * Preventive treatment prevents ALL future efficiency loss over equipment lifetime.
 *
 * KEY INSIGHT: This yields HIGHER lifetime ROI than restorative treatment because
 * you're preventing degradation from ever accumulating, compounding savings yearly.
 *
 * Example: 200-ton chiller treated Day 1
 * - Year 1: Prevent 16% degradation → save $6,318
 * - Year 10: Prevent 23% degradation → save $9,165
 * - Year 23: Prevent 24% degradation → save $9,204
 * - TOTAL: $209,703 avoided waste, 739% ROI
 *
 * @param {Object} params - Input parameters
 * @returns {Object} Preventive ROI projection with year-by-year breakdown
 */
export function calculatePreventiveROI({
  capacityTons,
  originalKwTon = null,
  runtimeHoursPerYear = 2500,
  electricityRate = 0.12,
  equipmentType = 'Chiller',
  equipmentLifeYears = null,
  model = 'corpus'
}) {
  const baseKwTon = originalKwTon || getTypicalKwTon(equipmentType)
  const lifeYears = equipmentLifeYears || getExpectedLife(equipmentType)

  let totalPreventedKwh = 0
  let totalPreventedCost = 0
  const yearlyData = []

  // Calculate avoided degradation for each year of equipment life
  for (let year = 0; year <= lifeYears; year++) {
    // What degradation WOULD exist without treatment
    const degradation = model === 'corpus'
      ? corpusDegradation(year)
      : ashraeDegradation(year)

    // Energy that WOULD BE wasted due to degradation (but is prevented)
    const degradedKwTon = baseKwTon * (1 + degradation)
    const preventedKwh = (degradedKwTon - baseKwTon) * capacityTons * runtimeHoursPerYear
    const preventedCost = preventedKwh * electricityRate

    totalPreventedKwh += preventedKwh
    totalPreventedCost += preventedCost

    yearlyData.push({
      year,
      degradationPreventedPct: (degradation * 100).toFixed(1),
      degradedKwTon: degradedKwTon.toFixed(3),
      preventedKwh: Math.round(preventedKwh),
      preventedCost: Math.round(preventedCost),
      cumulativeSavings: Math.round(totalPreventedCost)
    })
  }

  const treatmentCost = capacityTons * TREATMENT_COST_PER_TON
  const paybackYear = yearlyData.find(y => y.cumulativeSavings >= treatmentCost)?.year

  return {
    mode: 'PREVENTIVE',
    description: 'Avoided future degradation over equipment lifetime',
    treatmentCost: Math.round(treatmentCost),
    equipmentLifeYears: lifeYears,
    totalPreventedKwh: Math.round(totalPreventedKwh),
    totalPreventedCost: Math.round(totalPreventedCost),
    netLifetimeSavings: Math.round(totalPreventedCost - treatmentCost),
    lifetimeROIPct: Math.round(((totalPreventedCost - treatmentCost) / treatmentCost) * 100),
    avgAnnualSavings: Math.round(totalPreventedCost / lifeYears),
    paybackYear: paybackYear || null,
    paybackMonths: paybackYear ? (paybackYear * 12).toFixed(1) : 'N/A',
    yearlyBreakdown: yearlyData,
    dataSource: model === 'corpus'
      ? `Corpus model (n=${AGE_DATA_SAMPLE_SIZE} PE-certified measurements)`
      : 'ASHRAE model (~2%/year theoretical)',
    keyInsight: 'Preventive treatment yields ~2x higher lifetime ROI than waiting for degradation to accumulate'
  }
}

/**
 * Compare Preventive vs Restorative ROI side-by-side
 *
 * @param {Object} params - Equipment parameters
 * @returns {Object} Both ROI calculations for comparison
 */
export function comparePreventiveVsRestorative({
  capacityTons,
  currentAgeYears,
  originalKwTon = null,
  runtimeHoursPerYear = 2500,
  electricityRate = 0.12,
  equipmentType = 'Chiller'
}) {
  // Calculate restorative ROI (treating at current age)
  const restorative = generateProjectionData({
    capacityTons,
    currentAgeYears,
    originalKwTon,
    runtimeHoursPerYear,
    electricityRate,
    equipmentType
  })

  // Calculate preventive ROI (as if treating at Day 1)
  const preventive = calculatePreventiveROI({
    capacityTons,
    originalKwTon,
    runtimeHoursPerYear,
    electricityRate,
    equipmentType,
    model: 'corpus'
  })

  // Normalize field names for consistency
  const restorativeDegradation = (corpusDegradation(currentAgeYears) * 100).toFixed(1)

  return {
    restorative: {
      ...restorative,
      degradationRestoredPct: restorativeDegradation,
      remainingLifeYears: restorative.projectionYears.length,
      lifetimeROIPct: restorative.lifetimeROI,  // Alias for consistency
      totalLifetimeSavings: restorative.totalLifetimeSavings,
      annualSavings: restorative.projectionYears[0]?.annualSavings || 0,
      summary: `Restore ${restorativeDegradation}% existing degradation over ${restorative.projectionYears.length} remaining years`
    },
    preventive: {
      ...preventive,
      summary: `Prevent ALL degradation over ${preventive.equipmentLifeYears} year equipment life`
    },
    comparison: {
      roiDifference: preventive.lifetimeROIPct - restorative.lifetimeROI,
      savingsDifference: preventive.netLifetimeSavings - restorative.totalLifetimeSavings,
      insight: preventive.lifetimeROIPct > restorative.lifetimeROI
        ? `Preventive treatment yields ${preventive.lifetimeROIPct - restorative.lifetimeROI}% higher lifetime ROI`
        : 'Equipment already has significant degradation - restorative treatment recommended'
    }
  }
}

/**
 * Compare both models side by side
 */
export function compareModels(ageYears) {
  return {
    ashrae: getDegradationEstimate(ageYears, 'ashrae'),
    corpus: getDegradationEstimate(ageYears, 'corpus'),
    comparison: {
      difference: (corpusDegradation(ageYears) - ashraeDegradation(ageYears)) * 100,
      note: 'Corpus model typically shows higher degradation than ASHRAE theoretical model, reflecting real-world conditions'
    }
  }
}

// =============================================================================
// AVOIDED COST CALCULATION MODELS
// =============================================================================
// These functions calculate the comprehensive value of treatment beyond
// direct energy savings, including avoided degradation, maintenance,
// refrigerant losses, downtime, compressor failures, and life extension.

/**
 * kW/ton multipliers by equipment type (for energy calculations)
 */
export const EQUIPMENT_KWH_MULTIPLIERS = {
  'Chiller': 1.3,
  'RTU': 1.4,
  'CRAC': 1.5,
  'Split': 1.4
};

/**
 * Get age bucket from numeric age
 */
export function getAgeBucketFromAge(ageYears) {
  if (ageYears <= 5) return '0-5';
  if (ageYears <= 10) return '5-10';
  if (ageYears <= 15) return '10-15';
  if (ageYears <= 20) return '15-20';
  return '20+';
}

/**
 * Get degradation bracket from percentage
 */
function getDegradationBracket(degradationPct) {
  if (degradationPct < 15) return '0-15';
  if (degradationPct < 20) return '15-20';
  if (degradationPct < 25) return '20-25';
  if (degradationPct < 30) return '25-30';
  return '30+';
}

// -----------------------------------------------------------------------------
// 1. AVOIDED FUTURE DEGRADATION COSTS
// -----------------------------------------------------------------------------

/**
 * Calculate avoided degradation costs over projection period
 *
 * Without treatment: Equipment continues degrading 2-3%/year
 * With treatment: Efficiency stabilized at restored level
 *
 * @param {Object} params - Input parameters
 * @returns {Object} Avoided degradation costs
 */
export function calculateAvoidedDegradationCosts({
  equipmentType,
  currentAge,
  tonnage,
  operatingHours,
  electricityRate,
  years = 10
}) {
  const kwhPerTon = EQUIPMENT_KWH_MULTIPLIERS[equipmentType] || 1.3;
  const baseKwh = tonnage * kwhPerTon * operatingHours;
  const annualDegradationRate = 0.025; // 2.5%/year ASHRAE standard

  let untreatedCumulativeCost = 0;
  let treatedCumulativeCost = 0;

  // NOTE: We intentionally do NOT include currentDegradation here.
  // Energy Savings (Component 1) already counts the restoration of current degradation.
  // This function only counts FUTURE degradation that would occur without treatment.
  // This prevents double-counting the same efficiency loss.

  for (let year = 1; year <= years; year++) {
    // Without treatment: would degrade at 2.5%/year from design efficiency
    // (current degradation already captured in Energy Savings component)
    const futureDegradation = Math.min(
      annualDegradationRate * year,
      0.25 // Max 25% additional over 10-year projection
    );
    const untreatedKwh = baseKwh * (1 + futureDegradation);
    untreatedCumulativeCost += untreatedKwh * electricityRate;

    // With treatment: maintained at design efficiency
    const treatedKwh = baseKwh;
    treatedCumulativeCost += treatedKwh * electricityRate;
  }

  return {
    untreatedCost: untreatedCumulativeCost,
    treatedCost: treatedCumulativeCost,
    avoidedCost: untreatedCumulativeCost - treatedCumulativeCost,
    annualizedAvoidedCost: (untreatedCumulativeCost - treatedCumulativeCost) / years,
    projectionYears: years
  };
}

// -----------------------------------------------------------------------------
// 2. AVOIDED PEAK DEMAND CHARGES
// -----------------------------------------------------------------------------

const DEFAULT_DEMAND_RATE = 12; // $/kW/month - typical commercial
const DEGRADATION_TO_DEMAND_FACTOR = 0.8; // Each % degradation = ~0.8% more kW

/**
 * Calculate avoided peak demand charges
 *
 * Degraded equipment draws more kW. Commercial customers pay demand charges
 * ($/kW) separate from energy charges ($/kWh). Often 30-50% of electric bill.
 */
export function calculateAvoidedDemandCharges({
  equipmentType,
  currentAge,
  tonnage,
  demandRate = DEFAULT_DEMAND_RATE,
  years = 10
}) {
  const kwhPerTon = EQUIPMENT_KWH_MULTIPLIERS[equipmentType] || 1.3;
  const baseKw = tonnage * kwhPerTon;

  // NOTE: We intentionally do NOT include currentDegradation here.
  // Energy Savings (Component 1) already counts the efficiency restoration.
  // This function only counts FUTURE demand increases that would occur without treatment.
  const annualDegradationRate = 0.025;

  let untreatedDemandCost = 0;
  let treatedDemandCost = 0;

  for (let year = 1; year <= years; year++) {
    // Future degradation only (current degradation counted in Energy Savings)
    const futureDegradation = Math.min(
      annualDegradationRate * year,
      0.25
    );
    const untreatedKw = baseKw * (1 + futureDegradation * DEGRADATION_TO_DEMAND_FACTOR);
    untreatedDemandCost += untreatedKw * demandRate * 12;

    const treatedKw = baseKw;
    treatedDemandCost += treatedKw * demandRate * 12;
  }

  return {
    untreatedDemandCost,
    treatedDemandCost,
    avoidedDemandCost: untreatedDemandCost - treatedDemandCost,
    annualizedAvoidedDemand: (untreatedDemandCost - treatedDemandCost) / years
  };
}

// -----------------------------------------------------------------------------
// 3. AVOIDED MAINTENANCE COSTS
// -----------------------------------------------------------------------------

const MAINTENANCE_COST_BY_AGE = {
  'Chiller': { '0-5': 8, '5-10': 15, '10-15': 35, '15-20': 65, '20+': 100 },
  'RTU': { '0-5': 5, '5-10': 12, '10-15': 28, '15-20': 50, '20+': 75 },
  'CRAC': { '0-5': 6, '5-10': 14, '10-15': 32, '15-20': 55, '20+': 85 },
  'Split': { '0-5': 4, '5-10': 10, '10-15': 22, '15-20': 40, '20+': 60 }
};

const TREATMENT_MAINTENANCE_REDUCTION = 0.30; // 30% reduction

/**
 * Calculate avoided maintenance escalation costs
 *
 * Treatment restores efficiency, reducing compressor strain and failures.
 */
export function calculateAvoidedMaintenanceCosts({
  equipmentType,
  currentAge,
  tonnage,
  years = 10
}) {
  const costTable = MAINTENANCE_COST_BY_AGE[equipmentType] || MAINTENANCE_COST_BY_AGE['RTU'];

  let untreatedTotal = 0;
  let treatedTotal = 0;

  for (let year = 0; year < years; year++) {
    const ageAtYear = currentAge + year;
    const bracket = getAgeBucketFromAge(ageAtYear);
    const costPerTon = costTable[bracket] || 50;

    untreatedTotal += costPerTon * tonnage;
    treatedTotal += costPerTon * tonnage * (1 - TREATMENT_MAINTENANCE_REDUCTION);
  }

  return {
    untreatedMaintenanceCost: untreatedTotal,
    treatedMaintenanceCost: treatedTotal,
    avoidedMaintenanceCost: untreatedTotal - treatedTotal,
    annualizedAvoidedMaintenance: (untreatedTotal - treatedTotal) / years
  };
}

// -----------------------------------------------------------------------------
// 4. AVOIDED REFRIGERANT LOSSES
// -----------------------------------------------------------------------------

const REFRIGERANT_COST_PER_LB = {
  'R-410A': 25,
  'R-22': 50,    // Phased out, expensive
  'R-134a': 20,
  'R-407C': 22,
  'default': 25
};

const LBS_PER_TON = {
  'Chiller': 2.5,
  'RTU': 3.0,
  'CRAC': 3.5,
  'Split': 3.0
};

const LEAK_RATE_BY_AGE = {
  '0-5': 0.02,
  '5-10': 0.05,
  '10-15': 0.08,
  '15-20': 0.12,
  '20+': 0.18
};

const TREATMENT_LEAK_REDUCTION = 0.40; // 40% reduction

/**
 * Calculate avoided refrigerant loss costs
 *
 * Stressed/degraded systems leak more. Treatment reduces system stress.
 */
export function calculateAvoidedRefrigerantCosts({
  equipmentType,
  currentAge,
  tonnage,
  refrigerantType = 'default',
  years = 10
}) {
  const chargeAmount = tonnage * (LBS_PER_TON[equipmentType] || 3.0);
  const costPerLb = REFRIGERANT_COST_PER_LB[refrigerantType] || 25;

  let untreatedRefrigerantCost = 0;
  let treatedRefrigerantCost = 0;

  for (let year = 0; year < years; year++) {
    const ageAtYear = currentAge + year;
    const bracket = getAgeBucketFromAge(ageAtYear);
    const leakRate = LEAK_RATE_BY_AGE[bracket] || 0.08;

    const lostLbs = chargeAmount * leakRate;
    untreatedRefrigerantCost += lostLbs * costPerLb;

    const treatedLostLbs = chargeAmount * leakRate * (1 - TREATMENT_LEAK_REDUCTION);
    treatedRefrigerantCost += treatedLostLbs * costPerLb;
  }

  return {
    untreatedRefrigerantCost,
    treatedRefrigerantCost,
    avoidedRefrigerantCost: untreatedRefrigerantCost - treatedRefrigerantCost,
    annualizedAvoidedRefrigerant: (untreatedRefrigerantCost - treatedRefrigerantCost) / years
  };
}

// -----------------------------------------------------------------------------
// 5. AVOIDED EXTENDED RUNTIME COSTS (Corpus-Validated)
// -----------------------------------------------------------------------------
// Replaces Supplemental Cooling - based on user feedback that facilities
// run equipment longer rather than add rental cooling.
//
// Concept: Degraded equipment loses capacity and must run LONGER to meet load.
// This is separate from efficiency loss (more kWh/hour) - it's MORE HOURS.
//
// Modeling assumptions:
// - Only applies when degradation > 15%
// - Runtime increase = 50% of degradation percentage (capacity-to-runtime factor)
// - Example: 20% degraded → 10% longer runtime

const RUNTIME_EXTENSION_FACTOR = 0.50; // 50% capacity-to-runtime factor
const EXTENDED_RUNTIME_THRESHOLD = 0.15; // Only kicks in above 15% degradation

/**
 * Calculate avoided extended runtime costs
 *
 * Degraded equipment loses capacity and must run LONGER to meet the cooling load.
 * This is additional hours of operation beyond normal, separate from efficiency loss.
 *
 * NOT double-counting:
 * - Energy Savings = same hours × more kWh/hour (inefficiency)
 * - Extended Runtime = more hours × baseline kWh/hour (capacity compensation)
 *
 * @param {Object} params - Input parameters
 * @returns {Object} Avoided extended runtime costs
 */
export function calculateAvoidedExtendedRuntimeCosts({
  equipmentType,
  currentAge,
  tonnage,
  operatingHours,
  electricityRate,
  years = 10
}) {
  const kwhPerTon = EQUIPMENT_KWH_MULTIPLIERS[equipmentType] || 1.3;
  const baseKwhPerHour = tonnage * kwhPerTon;
  const currentDegradation = corpusDegradation(currentAge);
  const annualDegradationRate = 0.025;

  let untreatedRuntimeCost = 0;
  // With treatment: restored capacity, no extra runtime needed
  const treatedRuntimeCost = 0;

  for (let year = 1; year <= years; year++) {
    const untreatedDegradation = Math.min(
      currentDegradation + (annualDegradationRate * year),
      0.40
    );

    // Only apply extended runtime above threshold
    if (untreatedDegradation > EXTENDED_RUNTIME_THRESHOLD) {
      const effectiveDegradation = untreatedDegradation - EXTENDED_RUNTIME_THRESHOLD;
      const runtimeExtension = effectiveDegradation * RUNTIME_EXTENSION_FACTOR;
      const extraHours = operatingHours * runtimeExtension;
      const extraKwh = extraHours * baseKwhPerHour;
      untreatedRuntimeCost += extraKwh * electricityRate;
    }
  }

  return {
    untreatedRuntimeCost,
    treatedRuntimeCost,
    avoidedRuntimeCost: untreatedRuntimeCost,
    annualizedAvoidedRuntime: untreatedRuntimeCost / years
  };
}

// Legacy alias for backwards compatibility
export function calculateAvoidedSupplementalCosts(params) {
  // Map to new function with required operatingHours and electricityRate
  const result = calculateAvoidedExtendedRuntimeCosts({
    ...params,
    operatingHours: params.operatingHours || 2500,
    electricityRate: params.electricityRate || 0.12
  });
  return {
    untreatedSupplementalCost: result.untreatedRuntimeCost,
    treatedSupplementalCost: result.treatedRuntimeCost,
    avoidedSupplementalCost: result.avoidedRuntimeCost,
    annualizedAvoidedSupplemental: result.annualizedAvoidedRuntime
  };
}

// -----------------------------------------------------------------------------
// 6. AVOIDED DOWNTIME/FAILURE COSTS
// -----------------------------------------------------------------------------

const MTBF_BY_AGE = {
  'Chiller': { '0-5': 50000, '5-10': 35000, '10-15': 20000, '15-20': 12000, '20+': 8000 },
  'RTU': { '0-5': 30000, '5-10': 22000, '10-15': 15000, '15-20': 10000, '20+': 6000 },
  'CRAC': { '0-5': 40000, '5-10': 30000, '10-15': 20000, '15-20': 12000, '20+': 7000 },
  'Split': { '0-5': 25000, '5-10': 18000, '10-15': 12000, '15-20': 8000, '20+': 5000 }
};

const FAILURE_COST = {
  'Chiller': 5000,
  'RTU': 1500,
  'CRAC': 3000,
  'Split': 800
};

const TREATMENT_MTBF_IMPROVEMENT = 1.35; // 35% longer between failures

/**
 * Calculate avoided downtime/failure costs
 *
 * Degraded equipment fails more often. Each failure has costs:
 * repair parts, labor, and business disruption.
 */
export function calculateAvoidedDowntimeCosts({
  equipmentType,
  currentAge,
  operatingHours,
  years = 10
}) {
  const mtbfTable = MTBF_BY_AGE[equipmentType] || MTBF_BY_AGE['RTU'];
  const costPerFailure = FAILURE_COST[equipmentType] || 1500;

  let untreatedFailureCost = 0;
  let treatedFailureCost = 0;

  for (let year = 0; year < years; year++) {
    const ageAtYear = currentAge + year;
    const bracket = getAgeBucketFromAge(ageAtYear);
    const mtbf = mtbfTable[bracket] || 15000;

    // Guard against division by zero
    if (mtbf <= 0) continue;

    const untreatedFailures = operatingHours / mtbf;
    untreatedFailureCost += untreatedFailures * costPerFailure;

    const treatedMtbf = mtbf * TREATMENT_MTBF_IMPROVEMENT;
    const treatedFailures = operatingHours / treatedMtbf;
    treatedFailureCost += treatedFailures * costPerFailure;
  }

  return {
    untreatedFailureCost,
    treatedFailureCost,
    avoidedFailureCost: untreatedFailureCost - treatedFailureCost,
    annualizedAvoidedDowntime: (untreatedFailureCost - treatedFailureCost) / years
  };
}

// -----------------------------------------------------------------------------
// 7. AVOIDED COMPRESSOR FAILURE COSTS
// -----------------------------------------------------------------------------

const COMPRESSOR_COST = {
  'Chiller': 15000,
  'RTU': 2500,
  'CRAC': 4000,
  'Split': 1500
};

const COMPRESSOR_FAILURE_PROBABILITY = {
  '0-5': 0.01,
  '5-10': 0.03,
  '10-15': 0.08,
  '15-20': 0.15,
  '20+': 0.25
};

const TREATMENT_COMPRESSOR_PROTECTION = 0.50; // 50% reduction in failure risk

/**
 * Calculate avoided compressor failure costs
 *
 * Compressor failure is the #1 cause of HVAC replacement.
 * Treatment reduces thermal stress, lowering failure probability.
 */
export function calculateAvoidedCompressorCosts({
  equipmentType,
  currentAge,
  years = 10
}) {
  const compressorCost = COMPRESSOR_COST[equipmentType] || 2500;

  let untreatedCompressorRisk = 0;
  let treatedCompressorRisk = 0;

  for (let year = 0; year < years; year++) {
    const ageAtYear = currentAge + year;
    const bracket = getAgeBucketFromAge(ageAtYear);
    const failureProbability = COMPRESSOR_FAILURE_PROBABILITY[bracket] || 0.08;

    untreatedCompressorRisk += failureProbability * compressorCost;
    treatedCompressorRisk += failureProbability * (1 - TREATMENT_COMPRESSOR_PROTECTION) * compressorCost;
  }

  return {
    untreatedCompressorRisk,
    treatedCompressorRisk,
    avoidedCompressorCost: untreatedCompressorRisk - treatedCompressorRisk,
    annualizedAvoidedCompressor: (untreatedCompressorRisk - treatedCompressorRisk) / years
  };
}

// -----------------------------------------------------------------------------
// 8. LIFE EXTENSION VALUE (Enhanced with Cascading Degradation Effect)
// -----------------------------------------------------------------------------
//
// THE CASCADING DEGRADATION EFFECT:
// When equipment loses capacity due to thermal degradation, it creates a vicious cycle:
//
// 1. Capacity Loss (e.g., 20% degradation)
//       ↓
// 2. System runs LONGER to meet same cooling load
//    (100% ÷ 80% remaining capacity = 25% more runtime)
//       ↓
// 3. Components wear out FASTER (designed for X hours, running X*1.25)
//    - Compressors
//    - Fan motors
//    - Contactors
//    - Bearings
//       ↓
// 4. Equipment fails BEFORE expected life
//    (5-year-old system expected to last 15 years might only last 12)
//       ↓
// 5. Earlier capital replacement required ($50K-150K+ for chillers)
//
// TOTAL LIFE EXTENSION VALUE =
//   Avoided Accelerated Wear (equipment dying early) +
//   Traditional Life Extension (20% longer after treatment)
// -----------------------------------------------------------------------------

const REPLACEMENT_COST_PER_TON = {
  'Chiller': 1500,
  'RTU': 400,
  'CRAC': 600,
  'Split': 350
};

// Source: roi_calculator_logic.json line 113: "20% extended equipment life"
const LIFE_EXTENSION_FACTOR = 1.20; // 20% longer life (corpus-validated)

// Default replacement cycle - can be overridden by user
const DEFAULT_REPLACEMENT_CYCLE_YEARS = 10;

/**
 * Calculate life extension value with cascading degradation effect
 *
 * This enhanced model captures two sources of value:
 * 1. AVOIDED ACCELERATED WEAR: Degraded equipment runs longer to compensate for
 *    capacity loss, wearing out components faster and dying before expected life.
 *    Treatment restores capacity, eliminating the extra runtime and accelerated wear.
 *
 * 2. TRADITIONAL LIFE EXTENSION: Treatment extends life by 20% beyond normal
 *    expected life (corpus-validated from field data).
 *
 * Example: 5-year-old chiller with 20% degradation
 * - Without treatment (accelerated): Replace at year 8 (age 13) due to extra wear
 * - Without treatment (normal): Replace at year 10 (age 15) if no extra runtime
 * - With treatment (extended): Replace at year 12 (age 17) with 20% extension
 *
 * Value = NPV(normal) - NPV(accelerated) + NPV(extended) - NPV(normal)
 *       = Avoided early death + Deferral benefit
 *
 * @param {Object} params - Input parameters
 * @param {string} params.equipmentType - Equipment type for life expectancy lookup
 * @param {number} params.currentAge - Current equipment age in years
 * @param {number} params.tonnage - Equipment capacity in tons
 * @param {number} params.degradationPct - Current degradation percentage (0-100 or 0-1)
 * @param {number} params.replacementCycleYears - Company's replacement cycle (default 10)
 * @param {number} params.discountRate - Discount rate for NPV (default 8%)
 * @returns {Object} Life extension value breakdown with explanation
 */
export function calculateLifeExtensionValue({
  equipmentType,
  currentAge,
  tonnage,
  degradationPct = null,
  replacementCycleYears = DEFAULT_REPLACEMENT_CYCLE_YEARS,
  discountRate = 0.08
}) {
  // Normalize degradation to decimal (handle both 0-1 and 0-100 formats)
  let degradation = degradationPct;
  if (degradation === null || degradation === undefined) {
    // Estimate from age using corpus model
    degradation = corpusDegradation(currentAge);
  } else if (degradation > 1) {
    // Convert percentage to decimal
    degradation = degradation / 100;
  }

  // Use user's replacement cycle or ASHRAE expected life, whichever is shorter
  const ashraeLife = getExpectedLife(equipmentType);
  const expectedLife = Math.min(replacementCycleYears, ashraeLife);
  const remainingLife = Math.max(expectedLife - currentAge, 1);

  const replacementCost = tonnage * (REPLACEMENT_COST_PER_TON[equipmentType] || 500);

  // -----------------------------------------------------------------------------
  // CASCADING DEGRADATION CALCULATION
  // -----------------------------------------------------------------------------

  // Extra runtime factor: how much longer equipment must run to meet same load
  // Formula: 1 / (1 - capacityLoss) - 1
  // Example: 20% degradation → 1/(1-0.20) - 1 = 1/0.80 - 1 = 0.25 = 25% more runtime
  const extraRuntimeFactor = degradation > 0 ? (1 / (1 - degradation)) - 1 : 0;

  // Accelerated wear factor: extra runtime accelerates component wear proportionally
  // Equipment designed for X hours over Y years, running X*(1+extra) means reaching
  // design-life (Y * (1+extra)) sooner, or equivalently: Y / (1+extra) years
  const accelerationFactor = 1 + extraRuntimeFactor;

  // Calculate three replacement scenarios:
  // 1. Untreated with accelerated wear (equipment dies early due to extra runtime)
  const acceleratedRemainingLife = remainingLife / accelerationFactor;
  const acceleratedReplacementYear = Math.max(acceleratedRemainingLife, 0.5);

  // 2. Untreated without extra runtime (theoretical - if no capacity loss)
  const normalReplacementYear = remainingLife;

  // 3. Treated with life extension (capacity restored + 20% extension)
  const treatedReplacementYear = remainingLife * LIFE_EXTENSION_FACTOR;

  // -----------------------------------------------------------------------------
  // NPV CALCULATIONS
  // -----------------------------------------------------------------------------

  // NPV of replacement at each scenario
  const npvAccelerated = replacementCost / Math.pow(1 + discountRate, acceleratedReplacementYear);
  const npvNormal = replacementCost / Math.pow(1 + discountRate, normalReplacementYear);
  const npvTreated = replacementCost / Math.pow(1 + discountRate, treatedReplacementYear);

  // Value components
  // 1. Value from avoiding accelerated wear (equipment not dying early)
  const avoidedAcceleratedWearValue = npvAccelerated - npvNormal;

  // 2. Value from traditional life extension (20% deferral)
  const traditionalLifeExtensionValue = npvNormal - npvTreated;

  // Total life extension value
  const totalLifeExtensionValue = avoidedAcceleratedWearValue + traditionalLifeExtensionValue;

  // Annualized for comparison with other annual metrics
  // Use proper present value annuity factor instead of simple division
  const pvAnnuityFactor = treatedReplacementYear > 0 && discountRate > 0
    ? (1 - Math.pow(1 + discountRate, -treatedReplacementYear)) / discountRate
    : treatedReplacementYear;
  const annualizedLifeExtensionValue = totalLifeExtensionValue / Math.max(pvAnnuityFactor, 1);

  return {
    // Input parameters (for transparency)
    inputs: {
      equipmentType,
      currentAge,
      tonnage,
      degradationPct: (degradation * 100).toFixed(1),
      replacementCycleYears,
      discountRate
    },

    // Equipment life scenarios
    expectedLifeYears: expectedLife,
    remainingLifeYears: remainingLife,

    // Cascading degradation effect
    cascadingDegradation: {
      capacityLossPct: (degradation * 100).toFixed(1),
      extraRuntimePct: (extraRuntimeFactor * 100).toFixed(1),
      acceleratedRemainingLife: acceleratedRemainingLife.toFixed(1),
      normalRemainingLife: normalReplacementYear.toFixed(1),
      extendedRemainingLife: treatedReplacementYear.toFixed(1),
      explanation: `${(degradation * 100).toFixed(0)}% capacity loss → ${(extraRuntimeFactor * 100).toFixed(0)}% more runtime → equipment wears out ${((1 - acceleratedRemainingLife/normalReplacementYear) * 100).toFixed(0)}% faster`
    },

    // Financial values
    replacementCost,
    npv: {
      accelerated: Math.round(npvAccelerated),
      normal: Math.round(npvNormal),
      treated: Math.round(npvTreated)
    },

    // Value breakdown
    valueBreakdown: {
      avoidedAcceleratedWear: Math.round(avoidedAcceleratedWearValue),
      traditionalLifeExtension: Math.round(traditionalLifeExtensionValue),
      total: Math.round(totalLifeExtensionValue)
    },

    // Legacy fields for backward compatibility
    extendedLifeYears: treatedReplacementYear,
    yearsDeferredReplacement: treatedReplacementYear - acceleratedReplacementYear,
    npvOfDeferral: Math.round(totalLifeExtensionValue),
    annualizedLifeExtensionValue: Math.round(annualizedLifeExtensionValue),

    // Model explanation for reports
    modelExplanation: {
      title: 'Cascading Degradation Effect',
      summary: 'Degraded equipment runs longer to meet cooling load, accelerating wear and shortening life.',
      components: [
        {
          name: 'Avoided Accelerated Wear',
          value: Math.round(avoidedAcceleratedWearValue),
          description: `Equipment with ${(degradation * 100).toFixed(0)}% degradation runs ${(extraRuntimeFactor * 100).toFixed(0)}% longer, wearing out ${((1 - acceleratedRemainingLife/normalReplacementYear) * 100).toFixed(0)}% faster. Treatment restores capacity, preventing early failure.`
        },
        {
          name: 'Life Extension Deferral',
          value: Math.round(traditionalLifeExtensionValue),
          description: `Treatment extends equipment life by 20%, deferring replacement from year ${normalReplacementYear.toFixed(0)} to year ${treatedReplacementYear.toFixed(0)}.`
        }
      ],
      source: 'Corpus-validated: roi_calculator_logic.json + ASHRAE equipment life data'
    }
  };
}

// -----------------------------------------------------------------------------
// 9. MISSION-CRITICAL PROTECTION VALUE
// -----------------------------------------------------------------------------
//
// CONCEPT: HVAC is mission-critical infrastructure. When treatment improves
// reliability (35% longer MTBF), the VALUE depends on what happens when
// cooling fails. A data center losing $50,000/hour of downtime values
// reliability very differently than a standard office at $100/hour.
//
// THE BUSINESS IMPACT CHAIN:
// Equipment Failure → Cooling Stops → Business Operations Impacted
//    │
//    ├─ Data Center: Servers overheat → shutdown → SLA breach → $50K+/hour
//    ├─ Healthcare: Patient areas affected → regulatory violation → $10K+/hour
//    ├─ Pharmaceutical: Cold chain break → batch loss → $25K+/hour
//    └─ Standard Office: Comfort complaints → minimal impact → $100/hour
//
// CALCULATION:
// Mission-Critical Protection =
//   (Failures Avoided per Year) × (Avg Repair Hours) × ($/Hour Downtime Cost)
//
// Where:
// - Failures avoided = MTBF improvement (35%) reduces expected failures
// - Avg repair hours = Equipment-specific (Chiller: 24hrs, RTU: 8hrs, etc.)
// - $/Hour = From facility type selection or custom input
// -----------------------------------------------------------------------------

/**
 * Facility downtime cost per hour by type
 * These represent the operational cost of HVAC failure, not repair costs
 */
export const FACILITY_DOWNTIME_COST = {
  commercial: 100,       // Standard office - comfort complaints
  retail: 500,           // Lost sales, customer complaints
  manufacturing: 5000,   // Production halt, WIP spoilage
  healthcare: 10000,     // Patient safety, CMS penalties
  datacenter: 50000,     // SLA penalties, server shutdown
  pharmaceutical: 25000, // Batch loss, FDA compliance
  financial: 100000      // Trading halt, regulatory penalties
};

/**
 * Average repair time in hours by equipment type
 * Includes time to diagnose, source parts, and complete repair
 */
export const REPAIR_TIME_HOURS = {
  'Chiller': 24,    // Major equipment, parts may need ordering
  'RTU': 8,         // Rooftop accessible, common parts
  'CRAC': 12,       // Data center units, faster response priority
  'Split': 6        // Simple systems, quick repair
};

/**
 * Calculate mission-critical protection value
 *
 * This captures the operational value of improved reliability for facilities
 * where HVAC failure has significant business impact (data centers, healthcare,
 * pharma, financial services, etc.).
 *
 * The value is SEPARATE from avoided repair costs (captured in avoided downtime).
 * This represents avoided OPERATIONAL DISRUPTION costs.
 *
 * @param {Object} params - Input parameters
 * @param {string} params.equipmentType - Equipment type for MTBF lookup
 * @param {number} params.currentAge - Current equipment age in years
 * @param {string} params.facilityType - Facility type from FACILITY_DOWNTIME_COST
 * @param {number} params.customDowntimeCostPerHour - Custom $/hour (overrides facilityType)
 * @param {number} params.operatingHours - Annual operating hours
 * @param {number} params.years - Projection period (default 10)
 * @returns {Object} Mission-critical protection value breakdown
 */
export function calculateMissionCriticalProtection({
  equipmentType,
  currentAge,
  facilityType = 'commercial',
  customDowntimeCostPerHour = null,
  operatingHours,
  years = 10
}) {
  // Get downtime cost per hour
  const downtimeCostPerHour = customDowntimeCostPerHour !== null
    ? customDowntimeCostPerHour
    : (FACILITY_DOWNTIME_COST[facilityType] || FACILITY_DOWNTIME_COST.commercial);

  // Get repair time for equipment type
  const avgRepairHours = REPAIR_TIME_HOURS[equipmentType] || 8;

  // Get MTBF table for equipment type
  const mtbfTable = MTBF_BY_AGE[equipmentType] || MTBF_BY_AGE['RTU'];

  let untreatedDowntimeCost = 0;
  let treatedDowntimeCost = 0;
  let totalFailuresAvoided = 0;
  let totalDowntimeHoursAvoided = 0;

  for (let year = 0; year < years; year++) {
    const ageAtYear = currentAge + year;
    const bracket = getAgeBucketFromAge(ageAtYear);
    const mtbf = mtbfTable[bracket] || 15000;

    // Guard against division by zero
    if (mtbf <= 0) continue;

    // Expected failures without treatment
    const untreatedFailures = operatingHours / mtbf;
    const untreatedDowntimeHours = untreatedFailures * avgRepairHours;
    untreatedDowntimeCost += untreatedDowntimeHours * downtimeCostPerHour;

    // Expected failures with treatment (35% longer MTBF)
    const treatedMtbf = mtbf * TREATMENT_MTBF_IMPROVEMENT;
    const treatedFailures = operatingHours / treatedMtbf;
    const treatedDowntimeHours = treatedFailures * avgRepairHours;
    treatedDowntimeCost += treatedDowntimeHours * downtimeCostPerHour;

    // Track avoided failures for reporting
    totalFailuresAvoided += (untreatedFailures - treatedFailures);
    totalDowntimeHoursAvoided += (untreatedDowntimeHours - treatedDowntimeHours);
  }

  const avoidedDowntimeCost = untreatedDowntimeCost - treatedDowntimeCost;
  const annualizedValue = avoidedDowntimeCost / years;

  // Build human-readable explanation
  const facilityLabel = facilityType.charAt(0).toUpperCase() + facilityType.slice(1);
  const costLabel = customDowntimeCostPerHour !== null
    ? `$${customDowntimeCostPerHour.toLocaleString()}/hour (custom)`
    : `$${downtimeCostPerHour.toLocaleString()}/hour (${facilityLabel})`;

  return {
    // Value results
    avoidedDowntimeCost: Math.round(avoidedDowntimeCost),
    annualizedValue: Math.round(annualizedValue),

    // Supporting metrics
    totalFailuresAvoided: totalFailuresAvoided.toFixed(2),
    totalDowntimeHoursAvoided: totalDowntimeHoursAvoided.toFixed(1),
    avgRepairHours,
    downtimeCostPerHour,
    facilityType: customDowntimeCostPerHour !== null ? 'custom' : facilityType,

    // Context for UI display
    explanation: `Reduced failure risk saves ${formatCurrency(annualizedValue)}/year in avoided operational disruption`,
    detailedExplanation: {
      line1: `MTBF improvement (35%) reduces failures by ${totalFailuresAvoided.toFixed(2)} over ${years} years`,
      line2: `At ${avgRepairHours} hours per repair × ${costLabel}`,
      line3: `Total avoided operational disruption: ${formatCurrency(avoidedDowntimeCost)}`
    },

    // Projection period
    projectionYears: years
  };
}

/**
 * Format currency for display
 */
function formatCurrency(value) {
  return '$' + Math.round(value).toLocaleString();
}

// -----------------------------------------------------------------------------
// COMPREHENSIVE AVOIDED COST CALCULATION
// -----------------------------------------------------------------------------

/**
 * Calculate all avoided costs for an equipment item
 *
 * This is the main entry point for comprehensive ROI analysis.
 * Returns all 8 avoided cost components plus totals.
 */
export function calculateAllAvoidedCosts({
  equipmentType,
  currentAge,
  tonnage,
  operatingHours,
  electricityRate,
  demandRate = DEFAULT_DEMAND_RATE,
  refrigerantType = 'default',
  discountRate = 0.08,
  years = 10
}) {
  const avoidedDegradation = calculateAvoidedDegradationCosts({
    equipmentType, currentAge, tonnage, operatingHours, electricityRate, years
  });

  const avoidedDemand = calculateAvoidedDemandCharges({
    equipmentType, currentAge, tonnage, demandRate, years
  });

  const avoidedMaintenance = calculateAvoidedMaintenanceCosts({
    equipmentType, currentAge, tonnage, years
  });

  const avoidedRefrigerant = calculateAvoidedRefrigerantCosts({
    equipmentType, currentAge, tonnage, refrigerantType, years
  });

  const avoidedSupplemental = calculateAvoidedSupplementalCosts({
    equipmentType, currentAge, tonnage, years
  });

  const avoidedDowntime = calculateAvoidedDowntimeCosts({
    equipmentType, currentAge, operatingHours, years
  });

  const avoidedCompressor = calculateAvoidedCompressorCosts({
    equipmentType, currentAge, years
  });

  // Get current degradation for life extension calculation
  const currentDegradation = corpusDegradation(currentAge);

  const lifeExtension = calculateLifeExtensionValue({
    equipmentType,
    currentAge,
    tonnage,
    degradationPct: currentDegradation,
    replacementCycleYears: years, // Use same projection period
    discountRate
  });

  // Total annualized avoided costs
  const totalAnnualizedAvoidedCosts =
    avoidedDegradation.annualizedAvoidedCost +
    avoidedDemand.annualizedAvoidedDemand +
    avoidedMaintenance.annualizedAvoidedMaintenance +
    avoidedRefrigerant.annualizedAvoidedRefrigerant +
    avoidedSupplemental.annualizedAvoidedSupplemental +
    avoidedDowntime.annualizedAvoidedDowntime +
    avoidedCompressor.annualizedAvoidedCompressor +
    lifeExtension.annualizedLifeExtensionValue;

  return {
    avoidedDegradation,
    avoidedDemand,
    avoidedMaintenance,
    avoidedRefrigerant,
    avoidedSupplemental,
    avoidedDowntime,
    avoidedCompressor,
    lifeExtension,
    summary: {
      totalAnnualizedAvoidedCosts,
      projectionYears: years,
      totalTenYearAvoidedCosts: totalAnnualizedAvoidedCosts * years
    }
  };
}

export default {
  DEGRADATION_BY_AGE,
  AGE_DATA_SAMPLE_SIZE,
  EQUIPMENT_LIFE_YEARS,
  TREATMENT_COST_PER_TON,
  EQUIPMENT_KWH_MULTIPLIERS,
  FACILITY_DOWNTIME_COST,
  REPAIR_TIME_HOURS,
  ashraeDegradation,
  corpusDegradation,
  getDegradationEstimate,
  getExpectedLife,
  getTypicalKwTon,
  getAgeBucketFromAge,
  calculateLifetimeSavings,
  generateProjectionData,
  calculatePreventiveROI,
  comparePreventiveVsRestorative,
  compareModels,
  // Avoided cost functions (corpus-validated)
  calculateAvoidedDegradationCosts,
  calculateAvoidedDemandCharges,
  calculateAvoidedExtendedRuntimeCosts, // Replaces Supplemental
  calculateLifeExtensionValue,
  calculateMissionCriticalProtection,   // Mission-critical reliability value
  // Legacy/unsupported functions (kept for backwards compatibility)
  calculateAvoidedMaintenanceCosts,     // Not corpus-validated
  calculateAvoidedRefrigerantCosts,     // Not corpus-validated
  calculateAvoidedSupplementalCosts,    // Legacy alias → ExtendedRuntime
  calculateAvoidedDowntimeCosts,        // Not corpus-validated
  calculateAvoidedCompressorCosts,      // Not corpus-validated
  calculateAllAvoidedCosts
}
